/**
 * PATH: packages/email-hub/src/feature/lib/syncSearchBanner.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SYNCSEARCHBANNER-001
 * PURPOSE: Pure render-gate (G2) for the "mailbox still syncing ?
 *          older mail may be missing from search" inline banner.
 * WHY: The historical backfill walker (useEmailBackfill) persists its
 *      cursor in localStorage; while it is mid-walk, older mail is
 *      genuinely not searchable yet and zero-result searches mislead.
 *      This gate reads the SAME BackfillState the walker writes
 *      (via useBackfillStateView) ? no new backend calls.
 * RELATED:
 *  - hooks/useEmailBackfill.ts: BackfillState producer + view hook.
 *  - components/SearchSyncBanner.tsx: the banner rendering this gate.
 */

import type { BackfillState } from '../hooks/useEmailBackfill'

/**
 * f(backfill state, search active, dismissed) ? show the banner?
 *
 *  - never started (last_run_at === 0, no chunks): hidden ? a mailbox
 *    that has not begun a walk (or has no connected account) should
 *    not warn about syncing.
 *  - complete: hidden ? history is exhausted, search is trustworthy.
 *  - mid-walk (started, not complete): shown while a search is active
 *    and the user has not dismissed it this session.
 */
export function shouldShowSyncSearchBanner(args: {
  state: Pick<BackfillState, 'complete' | 'last_run_at' | 'total_chunks'>
  searchActive: boolean
  dismissed: boolean
}): boolean {
  const { state, searchActive, dismissed } = args
  if (dismissed) return false
  if (!searchActive) return false
  if (state.complete) return false
  const started = state.last_run_at > 0 || state.total_chunks > 0
  return started
}

/**
 * G2 live-search extension: should the banner ALSO offer the
 * "Search Outlook for older mail" CTA? Only when the banner itself is
 * visible AND local recall is sparse (the caller computes sparseness
 * via useLiveSearch's shouldOfferLiveSearch — same gate, one source).
 */
export function shouldShowBannerLiveSearchCta(args: {
  bannerShown: boolean
  liveSearchOffered: boolean
}): boolean {
  return args.bannerShown && args.liveSearchOffered
}
